import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from 'components/icons';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CircularLoader from 'components/CircularLoader';
import Loader from 'components/Loader';
import ProcessCard, { INLINE_SPINNER_CLASS, ProcessCardConfig } from 'components/processes/ProcessCard';
import { useProcessStatusPolling } from 'components/processes/useProcessStatusPolling';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Card, CardContent } from 'components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import useAuth from 'hooks/useAuth';
import { useIsNetvisorIntegrationEnabledQuery } from 'store/api/netvisor-integration';
import { ProcessType, useLazyGetProcessHistoryQuery } from 'store/api/processes';
import { useIsSageHrEnabledQuery } from 'store/api/sage-hr-integration';
import { useIsSmailyIntegrationEnabledQuery } from 'store/api/smaily-integration';
import { useIsWintimeIntegrationEnabledQuery } from 'store/api/wintime-integration';
import { useIsWordPressEnabledQuery } from 'store/api/wordpress-integration';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { ProcessChip } from './ProcessChip';
import ProcessExecutionItemDrawer from './ProcessExecutionItemDrawer';
import { ProcessesTablePagination } from './ProcessesTablePagination';

/** The history table's cells, at the compact density: 8px of padding, the row's ends still 16px in. */
const HEAD_CELL_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, 'p-2');
const BODY_CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2');

/** The integrations whose processes show only while the integration is enabled for the tenant. */
type IntegrationKey = 'netvisor' | 'smaily' | 'wintime' | 'sageHr' | 'wordPress';

const INTEGRATION_KEYS: IntegrationKey[] = ['netvisor', 'smaily', 'wintime', 'sageHr', 'wordPress'];

/**
 * A card's process; `integration` ties it to the integration that shows or hides it, `flag` to the feature flag that
 * does, `showItems` to whether its history rows carry per-item counts.
 */
type ProcessConfig = ProcessCardConfig & { integration?: IntegrationKey; flag?: FeatureFlag; showItems?: boolean };

const ALL_PROCESS_CONFIGS: ProcessConfig[] = [
    {
        type: 'CLIENT_BLOCKING',
        titleId: 'processes.client-blocking.title',
        descriptionId: 'processes.client-blocking.description',
    },
    {
        type: 'CLIENT_BLOCKING_WARNING',
        titleId: 'processes.client-blocking-warning.title',
        descriptionId: 'processes.client-blocking-warning.description',
    },
    {
        type: 'INVOICE_AUTOGENERATION',
        titleId: 'processes.invoice-autogeneration.title',
        descriptionId: 'processes.invoice-autogeneration.description',
    },
    {
        type: 'COMPLIANCE_EVALUATION',
        titleId: 'processes.compliance-evaluation.title',
        descriptionId: 'processes.compliance-evaluation.description',
    },
    {
        type: 'INSIDER_FUNCTION_AND_REASON_FILL',
        titleId: 'processes.insider-function-and-reason-fill.title',
        descriptionId: 'processes.insider-function-and-reason-fill.description',
        flag: FeatureFlag.MATTER_INSIDER_LIST,
        showItems: true,
    },
    {
        type: 'NETVISOR_DIMENSION_SYNC',
        titleId: 'processes.netvisor-dimension-sync.title',
        descriptionId: 'processes.netvisor-dimension-sync.description',
        integration: 'netvisor',
        showItems: true,
    },
    {
        type: 'NETVISOR_PARTY_SYNC',
        titleId: 'processes.netvisor-party-sync.title',
        descriptionId: 'processes.netvisor-party-sync.description',
        integration: 'netvisor',
        showItems: true,
    },
    {
        type: 'NETVISOR_EXPENSE_POLL',
        titleId: 'processes.netvisor-expense-poll.title',
        descriptionId: 'processes.netvisor-expense-poll.description',
        integration: 'netvisor',
        showItems: true,
    },
    {
        type: 'NETVISOR_SALES_INVOICE_IMPORT',
        titleId: 'processes.netvisor-sales-invoice-import.title',
        descriptionId: 'processes.netvisor-sales-invoice-import.description',
        integration: 'netvisor',
        showItems: true,
    },
    {
        type: 'NETVISOR_PAYMENT_POLL',
        titleId: 'processes.netvisor-payment-poll.title',
        descriptionId: 'processes.netvisor-payment-poll.description',
        integration: 'netvisor',
        showItems: true,
    },
    {
        type: 'SMAILY_PROFILES_SYNC',
        titleId: 'processes.smaily-profiles-sync.title',
        descriptionId: 'processes.smaily-profiles-sync.description',
        integration: 'smaily',
    },
    {
        type: 'WINTIME_EXPENSE_IMPORT',
        titleId: 'processes.wintime-expense-import.title',
        descriptionId: 'processes.wintime-expense-import.description',
        integration: 'wintime',
        showItems: true,
    },
];

/** Integration processes without a card of their own: they are started elsewhere and appear in the history only. */
const HISTORY_ONLY_PROCESSES: {
    type: ProcessType;
    titleId: string;
    integration: IntegrationKey;
    showItems?: boolean;
}[] = [
    { type: 'SAGE_HR_SYNC', titleId: 'sage-hr.process-title', integration: 'sageHr', showItems: true },
    {
        type: 'SMAILY_SINGLE_PROFILE_SYNC',
        titleId: 'processes.smaily-single-profile-sync.title',
        integration: 'smaily',
    },
    { type: 'SMAILY_SUBSCRIBER_CLEANUP', titleId: 'processes.smaily-subscriber-cleanup.title', integration: 'smaily' },
    {
        type: 'SMAILY_OPT_OUT_STATUS_SYNC',
        titleId: 'processes.smaily-opt-out-status-sync.title',
        integration: 'smaily',
    },
    { type: 'WORDPRESS_EMPLOYEE_SYNC', titleId: 'wordpress.process-title', integration: 'wordPress', showItems: true },
    {
        type: 'WORDPRESS_SINGLE_EMPLOYEE_SYNC',
        titleId: 'wordpress.single-process-title',
        integration: 'wordPress',
        showItems: true,
    },
];

const ALL_LABELLED_PROCESSES: {
    type: ProcessType;
    titleId: string;
    integration?: IntegrationKey;
    flag?: FeatureFlag;
    showItems?: boolean;
}[] = [...ALL_PROCESS_CONFIGS, ...HISTORY_ONLY_PROCESSES];

/** The history filter labels of one integration, or of the processes that need none, less those of a flag that is off. */
const labelsOf = (
    integration: IntegrationKey | undefined,
    flagOn: (flag?: FeatureFlag) => boolean
): Partial<Record<ProcessType, string>> =>
    Object.fromEntries(
        ALL_LABELLED_PROCESSES.filter((p) => p.integration === integration && flagOn(p.flag)).map((p) => [
            p.type,
            p.titleId,
        ])
    );

const ITEMS_TO_SHOW_PROCESS_TYPES = new Set<ProcessType>(
    ALL_LABELLED_PROCESSES.filter((p) => p.showItems).map((p) => p.type)
);

const PROCESS_TYPE_LABEL_MAP: Record<ProcessType, string> = Object.fromEntries(
    ALL_LABELLED_PROCESSES.map((p) => [p.type, p.titleId])
) as Record<ProcessType, string>;

const ExecutionHistory: FC<{
    anyRunning: boolean;
    onSelectExecution: (execution: { id: number; processType: ProcessType }) => void;
    /** The types the filter offers: those of the integrations in use, and the ones that need none. */
    filterLabels: Record<ProcessType, string>;
}> = ({ anyRunning, onSelectExecution, filterLabels }) => {
    const intl = useIntl();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [processTypeFilter, setProcessTypeFilter] = useState<ProcessType | null>(null);
    const filterLabel = intl.formatMessage({ id: 'processes.history.filter', defaultMessage: 'Filter by type' });
    const prevAnyRunningRef = useRef(false);

    const [fetchHistory, { data, isLoading }] = useLazyGetProcessHistoryQuery();

    useEffect(() => {
        fetchHistory({ page, size: rowsPerPage, ...(processTypeFilter ? { processType: processTypeFilter } : {}) });
    }, [fetchHistory, page, rowsPerPage, processTypeFilter]);

    useEffect(() => {
        if (prevAnyRunningRef.current && !anyRunning) {
            fetchHistory({ page, size: rowsPerPage, ...(processTypeFilter ? { processType: processTypeFilter } : {}) });
        }
        prevAnyRunningRef.current = anyRunning;
    }, [anyRunning, fetchHistory, page, rowsPerPage, processTypeFilter]);

    const formatDateTime = (dateStr: string | null) => {
        if (!dateStr) return '—';
        return intl.formatDate(dateStr, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    };
    const isClickable = (execution: { processTypeCode: ProcessType; totalItems: number }) =>
        ITEMS_TO_SHOW_PROCESS_TYPES.has(execution.processTypeCode) && execution.totalItems > 0;
    const showItemColumn = data?.content?.some(isClickable);
    const columnCount = showItemColumn ? 5 : 4;

    return (
        <Card className="gap-0 py-0">
            <CardContent className="gap-0 p-5">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-h6">
                        <FormattedMessage id="processes.history.title" />
                    </h2>
                    <Select<ProcessType | null>
                        value={processTypeFilter}
                        onValueChange={(value) => {
                            setProcessTypeFilter(value);
                            setPage(0);
                        }}
                    >
                        <div className="relative">
                            <SelectTrigger aria-label={filterLabel} className="bg-paper min-w-[200px]">
                                <SelectValue>
                                    {(value: ProcessType | null) =>
                                        value ? <FormattedMessage id={filterLabels[value]} /> : filterLabel
                                    }
                                </SelectValue>
                            </SelectTrigger>
                            {/* The label sits in the field while no type is chosen and moves onto its
                                border once one is. */}
                            {processTypeFilter ? (
                                <span
                                    aria-hidden
                                    className="bg-paper text-fg-secondary pointer-events-none absolute -top-2 left-2 px-1 text-xs"
                                >
                                    {filterLabel}
                                </span>
                            ) : null}
                        </div>
                        <SelectContent>
                            <SelectItem value={null}>
                                <FormattedMessage id="processes.history.filter-all" defaultMessage="All processes" />
                            </SelectItem>
                            {(Object.keys(filterLabels) as ProcessType[]).map((type) => (
                                <SelectItem key={type} value={type}>
                                    <FormattedMessage id={filterLabels[type]} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="relative w-full overflow-auto">
                    <table className="w-full text-left text-sm">
                        <TableHeader className="[&_tr]:border-b-0">
                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableHead className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="processes.history.process-type" />
                                </TableHead>
                                <TableHead className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="processes.history.started-at" />
                                </TableHead>
                                <TableHead className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="processes.history.status" />
                                </TableHead>
                                {showItemColumn && (
                                    <TableHead className={HEAD_CELL_CLASS}>
                                        <FormattedMessage id="processes.history.items" />
                                    </TableHead>
                                )}
                                <TableHead className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="processes.history.started-by" />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading && (
                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableCell colSpan={columnCount} className={cn(BODY_CELL_CLASS, 'text-center')}>
                                        <CircularLoader
                                            className={cn(INLINE_SPINNER_CLASS, 'inline-flex [&>span]:size-6')}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading && data?.content?.length === 0 && (
                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableCell colSpan={columnCount} className={cn(BODY_CELL_CLASS, 'text-center')}>
                                        <p className="text-body1 text-fg-secondary">
                                            <FormattedMessage id="processes.history.no-data" />
                                        </p>
                                    </TableCell>
                                </TableRow>
                            )}
                            {data?.content?.map((execution) => (
                                <TableRow
                                    key={execution.id}
                                    className={cn(
                                        SIMPLE_TABLE_ROW_CLASS,
                                        isClickable(execution) && 'hover:bg-grey-100 cursor-pointer'
                                    )}
                                    onClick={() =>
                                        isClickable(execution) &&
                                        onSelectExecution({
                                            id: execution.id,
                                            processType: execution.processTypeCode,
                                        })
                                    }
                                >
                                    <TableCell className={BODY_CELL_CLASS}>
                                        {PROCESS_TYPE_LABEL_MAP[execution.processTypeCode] ? (
                                            <FormattedMessage id={PROCESS_TYPE_LABEL_MAP[execution.processTypeCode]} />
                                        ) : (
                                            <>{execution.processTypeCode}</>
                                        )}
                                    </TableCell>
                                    <TableCell className={BODY_CELL_CLASS}>
                                        {formatDateTime(execution.startedAt)}
                                    </TableCell>
                                    <TableCell className={BODY_CELL_CLASS}>
                                        {execution.success === null && (
                                            <ProcessChip
                                                color="info"
                                                icon={
                                                    <CircularLoader
                                                        className={cn(
                                                            INLINE_SPINNER_CLASS,
                                                            'ml-1 [&>span]:size-3 [&>span]:border-current [&>span]:border-t-transparent'
                                                        )}
                                                    />
                                                }
                                            >
                                                <FormattedMessage id="processes.history.status-running" />
                                            </ProcessChip>
                                        )}
                                        {execution.success === true && (
                                            <ProcessChip color="success" icon={<CheckCircleIcon />}>
                                                <FormattedMessage id="processes.history.status-success" />
                                            </ProcessChip>
                                        )}
                                        {execution.success === false && (
                                            <ProcessChip color="error" icon={<CancelIcon />}>
                                                <FormattedMessage id="processes.history.status-failed" />
                                            </ProcessChip>
                                        )}
                                    </TableCell>
                                    {showItemColumn && (
                                        <TableCell className={BODY_CELL_CLASS}>
                                            {isClickable(execution) && (
                                                <span className="text-body2">
                                                    {execution.processedItems > 0 && (
                                                        <ProcessChip color="success" className="mr-1">
                                                            <span data-testid="processed-count">
                                                                {execution.processedItems}
                                                            </span>{' '}
                                                            ✓
                                                        </ProcessChip>
                                                    )}
                                                    {execution.skippedItems > 0 && (
                                                        <ProcessChip outlined className="mr-1">
                                                            {`${execution.skippedItems} ${intl
                                                                .formatMessage({
                                                                    id: 'processes.items.status-skipped',
                                                                    defaultMessage: 'skipped',
                                                                })
                                                                .toLowerCase()}`}
                                                        </ProcessChip>
                                                    )}
                                                    {execution.failedItems > 0 && (
                                                        <ProcessChip color="error">
                                                            <span data-testid="failed-count">
                                                                {execution.failedItems}
                                                            </span>{' '}
                                                            ✗
                                                        </ProcessChip>
                                                    )}
                                                </span>
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell className={BODY_CELL_CLASS}>
                                        {execution.createdBy?.name ?? '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </table>
                </div>
                {data && (
                    <ProcessesTablePagination
                        count={data.totalElements}
                        page={page}
                        onPageChange={setPage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(value) => {
                            setRowsPerPage(value);
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                )}
            </CardContent>
        </Card>
    );
};

const ProcessesPage: FC = () => {
    const { statuses, isLoading: isLoadingStatuses, anyRunning } = useProcessStatusPolling();
    const { data: isNetvisorEnabled, isError: isNetvisorQueryError } = useIsNetvisorIntegrationEnabledQuery();
    const { data: isSageHrEnabled } = useIsSageHrEnabledQuery();
    const { data: isSmailyEnabled } = useIsSmailyIntegrationEnabledQuery();
    const { data: isWintimeEnabled } = useIsWintimeIntegrationEnabledQuery();
    const { data: isWordPressEnabled } = useIsWordPressEnabledQuery();
    const enabledFeatures = useAuth().user?.enabledFeatures ?? [];
    const enabled: Record<IntegrationKey, boolean> = {
        netvisor: isNetvisorEnabled === true,
        smaily: isSmailyEnabled === true,
        wintime: isWintimeEnabled === true,
        sageHr: isSageHrEnabled === true,
        wordPress: isWordPressEnabled === true,
    };
    const flagOn = (flag?: FeatureFlag) => !flag || enabledFeatures.includes(flag);
    const allCards = ALL_PROCESS_CONFIGS.filter((c) => (!c.integration || enabled[c.integration]) && flagOn(c.flag));
    const filterLabels = Object.assign(
        {},
        labelsOf(undefined, flagOn),
        ...INTEGRATION_KEYS.filter((key) => enabled[key]).map((key) => labelsOf(key, flagOn))
    ) as Record<ProcessType, string>;
    const [selectedExecution, setSelectedExecution] = useState<{
        id: number;
        processType: ProcessType;
    } | null>(null);

    // TODO: effect-event-handler skip — reacts to async RTK Query error flag, not a synchronous event.
    useEffect(() => {
        if (isNetvisorQueryError) {
            console.error(
                'ProcessesPage: useIsNetvisorIntegrationEnabledQuery failed — Netvisor cards hidden as fallback.'
            );
        }
    }, [isNetvisorQueryError]);

    if (isLoadingStatuses) {
        return <Loader />;
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-body1 text-fg-secondary">
                <FormattedMessage id="processes.page-description" />
            </p>
            <div className="new:gap-(--card-gap) flex flex-col gap-4">
                {allCards.map((config) => (
                    <ProcessCard key={config.type} config={config} isRunning={statuses?.[config.type] ?? false} />
                ))}
                <ExecutionHistory
                    anyRunning={anyRunning}
                    onSelectExecution={(execution) => setSelectedExecution(execution)}
                    filterLabels={filterLabels}
                />
            </div>
            <ProcessExecutionItemDrawer
                executionId={selectedExecution?.id ?? null}
                processType={selectedExecution?.processType ?? null}
                onClose={() => setSelectedExecution(null)}
            />
        </div>
    );
};

export default ProcessesPage;
